import React from "react"
import styled, { css } from "styled-components"

import { Icon, DropdownButton } from "quorumdesign"

import { QuorumTheme } from "app/static/frontend/design-constants"

const Card = styled.div`
    display: flex;
    align-items: flex-start;
    align-self: stretch;
    width: 100%;

    border-radius: 0.25rem;
    border: 1px solid ${QuorumTheme.colors.gray[3]};
    background-color: #fff;
    box-shadow: 0px 4px 8px -4px rgba(0, 0, 0, 0.16);
    overflow: hidden;

    ${({ isSelected }) =>
        isSelected &&
        css`
            border-radius: 8px;
            border: 1px solid ${QuorumTheme.colors.violet[8]};
            box-shadow: 0px 4px 12px -4px rgba(0, 0, 0, 0.2);
        `}
`

const Note = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 0 0;
    overflow: hidden;
`

const Type = styled.div`
    display: flex;
    padding: 1rem 0.75rem;
    align-items: flex-start;
    gap: 0.5rem;
    align-self: stretch;

    background-color: ${QuorumTheme.colors.gray[0]};
`

const Thumbnail = styled.img`
    height: 2rem;
    width: 2rem;

    border-radius: 1rem;
    border: 1px solid ${QuorumTheme.colors.violet[7]};
`

const Title = styled.div`
    padding: 0.75rem 0.25rem 0.5rem 1rem;
    gap: 0.25rem;
    align-self: stretch;

    color: ${QuorumTheme.colors.gray[9]};
    font-size: 1rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.5rem;

    b {
        font-weight: 500;
    }

    a {
        /* The links in the Note text are generated and saved on the backed :weary:,
        * but we don't want users going to Profiles yet, so just disable clicking/tapping on them.
        */
        color: ${QuorumTheme.colors.gray[7]};
        pointer-events: none;
    }
`

const Info = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    align-self: stretch;
    padding: 0.5rem 1rem;

    color: ${QuorumTheme.colors.gray[7]};
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    && > span {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        svg {
            width: 0.875rem;
            height: 0.875rem;
            color: ${QuorumTheme.colors.gray[5]};
        }
    }
`

// Styles MobileCard InfoContent anchor colors to be consistent with desktop
// search a tags even though they're not individually clickable via mobile search/MobileCard.
// The props.isHtml check is included since member and executive emails aren't
// isLink === true in executiveInlineSelector and memberInlineSelector.
const InfoContent = styled.span`
    word-break: break-word;

    a {
        color: ${(props) => (props.isLink || props.isHtml ? QuorumTheme.colors.violet[7] : QuorumTheme.colors.gray[7])};
        pointer-events: ${(props) => (props.isLink ? "inherit" : "none")};
    }
`

const Text = styled.div`
    padding: 0.5rem 1rem;
    align-self: stretch;

    color: ${QuorumTheme.colors.gray[9]};
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5rem;
`

const Actions = styled.div`
    display: flex;
    padding: 0rem 0.25rem 0.5rem 0.25rem;
    justify-content: space-between;
    align-items: flex-start;
    align-self: stretch;

    div:last-child {
        margin-left: auto;
    }
`

const Button = styled.a`
    && > svg {
        height: 1rem;
        width: 1rem;
        margin: 0.75rem;
    }
`

// Awful stopgap to satisfy big G while we wait for a full Search rebuild. Delete me ASAP.
export const MobileCard = (props) => (
    <Card
        onClick={props.cardInnerOnClick}
        role="button"
        tabIndex="0"
        onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                this.props.cardInnerOnClick()
            }
        }}
        isSelected={props.isSelected}
    >
        <Note>
            <Title>{props.firstLineText}</Title>
            <Info>
                {props.secondLineDS?.map((item) => (
                    <span key={item.value}>
                        <Icon icon={item.fa5Icon} iconFamily="far" />
                        <InfoContent
                            isLink={item.isLink}
                            isHtml={item.isHtml}
                            dangerouslySetInnerHTML={{ __html: item.value }}
                        />
                    </span>
                ))}
            </Info>
            <Text>{props.thirdLineText}</Text>
            <Actions>
                {props.options?.map((item) =>
                    item.isDropdown ? (
                        <DropdownButton
                            key={item.label}
                            isTiny
                            leftIcon={item.fa5_icon}
                            leftIconFamily={item.fa5_icon_family}
                            leftIconSize="14px"
                            menuOptions={item.menuOptions}
                            text={item.label}
                            type="tertiary"
                        />
                    ) : (
                        <div key={item.label || item.icon}>
                            <Button href={item.href} onClick={item.callback} role="button">
                                <Icon
                                    icon={item.fa5_icon}
                                    iconFamily={item.fa5_icon_family}
                                    color={item.iconColor || QuorumTheme.colors.gray[7]}
                                />
                            </Button>
                        </div>
                    ),
                )}
            </Actions>
        </Note>
        <Type>
            {/* Must use FA4 icons here, because that's what Custom Interaction Types have */}
            {props.imagePath ? (
                <Thumbnail src={props.imagePath} alt="Official Portrait" />
            ) : (
                <i className={props.iconNameClassName} style={{ color: QuorumTheme.colors.gray[7] }} />
            )}
        </Type>
    </Card>
)
